import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_CARD_TYPE, CARD_ELEMENT_TYPE } from '../../constants';
import CardElementFrame from '../CardElementFrame';
import CardElementAddress from '../CardElementAddress';
import CardElementText from '../CardElementText';
import CardElementPhoto from '../CardElementPhoto';
import CardElementSticker from '../CardElementSticker';
import CardElementBarCode from '../CardElementBarCode';
import CardElementNoPrint from '../CardElementNoPrint';
import { getCardElementPhotoOverlayElement } from '../CardElementPhoto/helpers';
import { getCardTemplateSizing } from '../AccountCard/helpers';

const propTypes = {
  className: PropTypes.string,
  cardSide: PropTypes.shape({
    id: PropTypes.string.isRequired,
    card_template: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      cardElement: PropTypes.object.isRequired,
      cardElementConfiguration: PropTypes.object.isRequired,
      side: PropTypes.string.isRequired,
      loading: PropTypes.bool,
    }).isRequired,
  ).isRequired,
  setElementState: PropTypes.func.isRequired,
  photosContext: PropTypes.shape({
    account_id: PropTypes.string,
    album_id: PropTypes.string,
    identity_id: PropTypes.string,
    roster_identity_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  viewOnly: PropTypes.bool,
  setSelectedRosterIdentityIds: PropTypes.func,
  printable: PropTypes.bool,
  onPhotoPpiWarning: PropTypes.func,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
  allowPhotoChange: PropTypes.bool,
  disablePhotoWatermarkOverlay: PropTypes.bool,
  defaultPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  onAddAccountCardElementReference: PropTypes.func,
  allowOrientationChange: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
  linkImageElements: PropTypes.bool,
  teamPhotos: PropTypes.array,
};

const CardSide = ({
  className,
  cardSide,
  elements,
  setElementState,
  photosContext,
  accountCard,
  viewOnly,
  setSelectedRosterIdentityIds,
  printable,
  onPhotoPpiWarning,
  mappings,
  allowPhotoChange = true,
  disablePhotoWatermarkOverlay,
  defaultPhoto,
  onAddAccountCardElementReference,
  watermarkSettings,
  allowOrientationChange,
  linkImageElements,
  teamPhotos,
}) => {
  const classes = classNames({
    [className]: !!className,
    'card-side': true,
    'auto-layout': accountCard.type === ACCOUNT_CARD_TYPE.AUTO_LAYOUT,
  });

  // Hack(ish): Enable text fitting on all text elements except
  // when they're part of standard Waldogram cards which should stay untouched.
  // Ideally one solution should cover all cases, both customly designed Cards
  // and Waldograms.
  const fitText = !!accountCard?.print_product_configuration;

  const ref = useRef(null);
  const { width, height, ratio } = getCardTemplateSizing(
    cardSide.card_template,
    accountCard?.type === ACCOUNT_CARD_TYPE.AUTO_LAYOUT
      ? {
          width: 16,
          height: 9,
        }
      : undefined,
  );

  return (
    <div
      className={classes}
      ref={ref}
      style={{ paddingBottom: `${ratio * 100}%` }}
    >
      {elements.map(element => {
        const { cardElement, cardElementConfiguration } = element;

        return (
          <CardElementFrame
            key={cardElement.id}
            element={element}
            teamPhotos={teamPhotos}
          >
            {cardElement.type === CARD_ELEMENT_TYPE.PHOTO && (
              <CardElementPhoto
                element={element}
                onChange={setElementState}
                overlayElement={getCardElementPhotoOverlayElement(
                  cardElement.id,
                  elements,
                )}
                photosContext={photosContext}
                accountCard={accountCard}
                viewOnly={viewOnly}
                cardWidth={width}
                cardHeight={height}
                printable={printable}
                onPpiWarning={onPhotoPpiWarning}
                mappings={mappings}
                allowPhotoChange={allowPhotoChange}
                disablePhotoWatermarkOverlay={disablePhotoWatermarkOverlay}
                defaultPhoto={defaultPhoto}
                onAddAccountCardElementReference={
                  onAddAccountCardElementReference
                }
                watermarkSettings={watermarkSettings}
                allowOrientationChange={allowOrientationChange}
                linkImageElements={linkImageElements}
                teamPhotos={teamPhotos}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.TEXT && (
              <CardElementText
                element={element}
                onChange={setElementState}
                accountCard={accountCard}
                viewOnly={viewOnly}
                fitText={fitText || cardElementConfiguration?.text_fit_enabled}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.ADDRESS && (
              <CardElementAddress
                element={element}
                onChange={setElementState}
                accountCard={accountCard}
                viewOnly={viewOnly}
                setSelectedRosterIdentityIds={setSelectedRosterIdentityIds}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.BARCODE && (
              <CardElementBarCode
                element={element}
                onChange={setElementState}
                accountCard={accountCard}
                viewOnly={viewOnly}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.STICKER && (
              <CardElementSticker
                element={element}
                onChange={setElementState}
                accountCard={accountCard}
                viewOnly={viewOnly}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.NO_PRINT && (
              <CardElementNoPrint
                element={element}
                accountCard={accountCard}
                viewOnly={viewOnly}
              />
            )}
          </CardElementFrame>
        );
      })}
    </div>
  );
};

CardSide.propTypes = propTypes;

export default CardSide;
